import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import AddCognitiveItem from './AddCognitiveItem';
import { COGNITIVE_SECTION, JOB_PROFILE_SURVEY_ITEM_TYPE, STANDARD_ITEM_TYPE } from './constants';
import { AccountContext } from '../../components/AccountContext';

const i18nMessages = defineMessages({
  itemType: {
    id: 'menu.item-bank-management.item-type',
    defaultMessage: 'Item Type',
  },
  itemAnswerType: {
    id: 'menu.item-bank-management.item-answer-type',
    defaultMessage: 'Item Answer Type',
  },
  itemCode: {
    id: 'menu.item-bank-management.item-code',
    defaultMessage: 'Item Code',
  },
  itemText: {
    id: 'menu.item-bank-management.item-text',
    defaultMessage: 'Item Text',
  },
  supportText: {
    id: 'menu.item-bank-management.support-text',
    defaultMessage: 'Support Text',
  },
  answers: {
    id: 'menu.item-bank-management.answers',
    defaultMessage: 'Answers',
  },
  actions: {
    id: 'menu.item-bank-management.actions',
    defaultMessage: 'Actions',
  },
});

const getContexts = (formValues, availableContexts) => {
  const contextValue = formValues.context;

  const contexts = [...availableContexts];

  const useAddedAContext = contextValue.type && contextValue.code && contextValue.content;

  if (useAddedAContext) {
    contexts.push({
      id: null,
      code: contextValue.code,
      itemType: STANDARD_ITEM_TYPE,
      sectionCode: COGNITIVE_SECTION,
    });
  }

  return contexts;
};

const AddCognitiveItems = ({ availableItemTypes, availableItemAnswerTypes, availableContexts }) => {
  const [index, setIndex] = useState(null);

  const currentAccount = useContext(AccountContext);
  const { formatMessage } = useIntl();
  const history = useHistory();

  const { values: parentFormValues, setFieldValue } = useFormikContext();

  const itemValues = parentFormValues.items.filter((item) => item.sectionCode === COGNITIVE_SECTION);

  const contexts = getContexts(parentFormValues, availableContexts);

  const initialValues =
    index != null
      ? {
          sectionCode: itemValues[index].sectionCode,
          type: itemValues[index].type,
          answerType: itemValues[index].answerType,
          code: itemValues[index].code,
          text: itemValues[index].text,
          contextId: itemValues[index].contextId,
          answers: itemValues[index].answers,
        }
      : {
          sectionCode: COGNITIVE_SECTION,
          type: '',
          answerType: '',
          code: '',
          text: '',
          contextId: '',
          answers: [],
        };

  const validationSchema = Yup.object().shape({
    type: Yup.string().required(),
    answerType: Yup.string().required(),
    code: Yup.string().required(),
    text: Yup.string().required(),
    answers: Yup.array().of(
      Yup.object().shape({
        text: Yup.string().required(),
      }),
    ),
  });

  const { section } = parentFormValues;

  const actionsBody = (data, { rowIndex }) => (
    <div className="align-center">
      <Button type="button" className="p-button-secondary" icon="pi pi-pencil" onClick={() => setIndex(rowIndex)} />
    </div>
  );

  const contextBody = (data) => {
    const contextOfItem = contexts.find((context) => context.code === data.contextCode);

    const contextCode = contextOfItem ? contextOfItem.code : '-';

    return <div className="align-center">{contextCode}</div>;
  };

  const answersBody = (data) => {
    const { type, answers } = data;

    if (type === JOB_PROFILE_SURVEY_ITEM_TYPE) {
      return '-';
    }

    const answersToDisplay = answers.map((answer) => (
      <div className="item-answers">
        <span>{answer.text}</span>
        <span>
          {answer.isCorrect && <i className="pi pi-check correct-answer" />}
          {!answer.isCorrect && <i className="pi pi-times wrong-answer" />}
        </span>
      </div>
    ));

    return <>{answersToDisplay}</>;
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, formikBag) => {
          formikBag.resetForm();

          if (index != null) {
            const updatedValues = itemValues.map((item, itemIndex) => {
              if (itemIndex === index) {
                return { ...values };
              }

              return item;
            });

            setFieldValue('items', updatedValues);
            setIndex(null);
          } else {
            setFieldValue('items', [...parentFormValues.items, { ...values }]);
          }
        }}
      >
        <Form>
          <AddCognitiveItem
            section={section}
            availableItemTypes={availableItemTypes}
            availableItemAnswerTypes={availableItemAnswerTypes}
            availableContexts={contexts}
          />
          <FormattedMessage id="item-bank-management.context-type.save-item-text" defaultMessage="Save Item Text">
            {(text) => <Button type="submit" className="p-button-secondary inner-form-submit-button" label={text} />}
          </FormattedMessage>
        </Form>
      </Formik>

      <DataTable value={itemValues} className="text-center">
        <Column field="type" header={formatMessage(i18nMessages.itemType)} />
        <Column field="answerType" header={formatMessage(i18nMessages.itemAnswerType)} />
        <Column field="code" header={formatMessage(i18nMessages.itemCode)} />
        <Column field="text" header={formatMessage(i18nMessages.itemText)} />
        <Column field="contextId" header={formatMessage(i18nMessages.supportText)} body={contextBody} />
        <Column field="answers" header={formatMessage(i18nMessages.answers)} body={answersBody} />
        <Column body={actionsBody} header={formatMessage(i18nMessages.actions)} />
      </DataTable>

      <div className="outer-form-submit-button">
        <FormattedMessage id="actions.next" defaultMessage="Next">
          {(text) => (
            <Button
              type="button"
              label={text}
              // disabled if there are less than 9 standard items and 5 jps items
              onClick={() => {
                history.push(`/accounts/${currentAccount}/add-itembank-assessment-content/add-report-texts`);
              }}
            />
          )}
        </FormattedMessage>
        <FormattedMessage id="actions.back" defaultMessage="Back">
          {(text) => (
            <Button type="button" onClick={() => history.goBack()} className="p-button-secondary" label={text} />
          )}
        </FormattedMessage>
      </div>
    </>
  );
};

AddCognitiveItems.propTypes = {
  availableItemTypes: PropTypes.arrayOf(PropTypes.any).isRequired,
  availableItemAnswerTypes: PropTypes.arrayOf(PropTypes.any).isRequired,
  availableContexts: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default AddCognitiveItems;
